import React, { useCallback, useState } from "react";
import { useTable } from "react-table";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import { ExpandingRow } from "../../components/table/expanding-row";
import { deadLetterTableColumns } from "../config/dead-letter-table-config";
import {
  DeadLetterFilters,
  DeadLetterPage,
  DeadLetterSummary,
} from "../model/dead-letter";
import { DeadLetterFiltersBar } from "./dead-letter-filters";
import { DeadLetterDetails } from "./dead-letter-details";

interface DeadLettersTableProps {
  data: DeadLetterPage;
  filters: DeadLetterFilters;
  resendingId: number | undefined;
  handleChangePage: (newPage: number) => void;
  handleChangeRowsPerPage: (rowsPerPage: number) => void;
  handleFilterChange: (change: Partial<DeadLetterFilters>) => void;
  onResend: (id: number) => void;
}

export const DeadLettersTable: React.FC<DeadLettersTableProps> = ({
  data,
  filters,
  resendingId,
  handleChangePage,
  handleChangeRowsPerPage,
  handleFilterChange,
  onResend,
}) => {
  const [toConfirm, setToConfirm] = useState<DeadLetterSummary>();

  const handleChangeRowsPerPageAdapter = useCallback(
    (event: any) => handleChangeRowsPerPage(parseInt(event.target.value, 10)),
    [handleChangeRowsPerPage]
  );
  const handleChangePageAdapter = useCallback(
    (_: any, newPage: number) => handleChangePage(newPage),
    [handleChangePage]
  );

  const confirmResend = useCallback(() => {
    if (toConfirm) {
      onResend(toConfirm.id);
    }
    setToConfirm(undefined);
  }, [toConfirm, onResend]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns: deadLetterTableColumns, data: data.content });

  return (
    <Paper>
      <DeadLetterFiltersBar filters={filters} onChange={handleFilterChange} />
      <TableContainer>
        <Table {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow
                {...headerGroup.getHeaderGroupProps()}
                key={headerGroup.getHeaderGroupProps().key}
              >
                {headerGroup.headers.map((column) => (
                  <TableCell
                    {...column.getHeaderProps()}
                    key={column.getHeaderProps().key}
                  >
                    {column.render("Header") as any}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={deadLetterTableColumns.length + 1}>
                  <Typography>No dead letters match the filters.</Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              prepareRow(row);
              const deadLetter = row.original;
              return (
                <ExpandingRow
                  key={"" + row.getRowProps().key + deadLetter.id}
                  renderSummary={() => (
                    <>
                      {row.cells.slice(1).map((cell) => (
                        <TableCell
                          {...cell.getCellProps()}
                          key={cell.getCellProps().key}
                        >
                          <Typography noWrap>
                            <>{cell.render("Cell")}</>
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<ReplayIcon />}
                          disabled={resendingId === deadLetter.id}
                          onClick={() => setToConfirm(deadLetter)}
                          data-testid={`resend-${deadLetter.id}`}
                        >
                          {resendingId === deadLetter.id
                            ? "Resending…"
                            : "Resend"}
                        </Button>
                      </TableCell>
                    </>
                  )}
                  renderDetails={() => <DeadLetterDetails id={deadLetter.id} />}
                  icons={[]}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={data.totalElements}
        rowsPerPage={data.size}
        page={data.page}
        onPageChange={handleChangePageAdapter}
        onRowsPerPageChange={handleChangeRowsPerPageAdapter}
      />
      <Dialog open={!!toConfirm} onClose={() => setToConfirm(undefined)}>
        <DialogTitle>Resend dead letter?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Dead letter {toConfirm?.id} will be published again to{" "}
            <strong>{toConfirm?.originalTopic ?? "its original topic"}</strong>.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }} data-testid="resend-fanout-warning">
            Every service subscribed to this topic receives it again, not only
            the one that failed. Resend only if those consumers handle a
            duplicate safely. The Pub/Sub attribute
            CloudPubSubDeadLetterSourceSubscription in the details names the
            service that failed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToConfirm(undefined)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmResend}
            data-testid="confirm-resend"
          >
            Resend
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
